<script setup lang="ts">
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/solid';
import MlDataPointValue, {
  type TDataPointValueAndType,
} from '@/components/molecules/MlDataPointValue.vue';
import type { PreparedPerYear } from '../../types';

type Props = {
  scopeSectionId?: string;
  scopeSectionName?: string;
  isScopeSectionCollapsed?: boolean;
  scopeSectionSpan?: number;
  sourceSectionName?: string;
  sourceSectionSpan: number;
  projectId: string;
  projectName: string;
  isProjectCollapsible: boolean;
  isProjectTopLevel: boolean;
  isProjectCollapsed: boolean;
  years: PreparedPerYear['years'];
  values: Record<string, TDataPointValueAndType>;
  hasBottomSpacing?: boolean;
};
withDefaults(defineProps<Props>(), {
  scopeSectionId: undefined,
  scopeSectionName: '',
  isScopeSectionCollapsed: false,
  scopeSectionSpan: 0,
  sourceSectionName: '',
  hasBottomSpacing: false,
});

defineEmits<{
  toggleProject: [id: string];
  toggleScope: [scopeSectionId: string];
}>();
</script>

<template>
  <tr>
    <th
      v-if="scopeSectionSpan > 0"
      :class="[
        'align-top',
        {
          'p-3': !hasBottomSpacing,
          'pt-3 px-3 pb-13': hasBottomSpacing,
        },
      ]"
      :rowspan="scopeSectionSpan"
    >
      <button
        v-if="scopeSectionId"
        class="inline-flex space-x-0.5 text-left"
        type="button"
        @click="$emit('toggleScope', scopeSectionId)"
      >
        <span class="shrink-0 w-4 h-4"
          ><ChevronRightIcon v-if="isScopeSectionCollapsed" /><ChevronDownIcon
            v-else
        /></span>
        <span>{{ scopeSectionName }}</span>
      </button>
      <div v-else>
        {{ scopeSectionName }}
      </div>
    </th>
    <td
      v-if="sourceSectionSpan > 0"
      :class="[
        'align-top',
        {
          'p-3': !hasBottomSpacing,
          'pt-3 px-3 pb-14': hasBottomSpacing,
        },
      ]"
      :rowspan="sourceSectionSpan"
    >
      {{ sourceSectionName }}
    </td>
    <td
      :class="[
        'align-top',
        {
          'p-3': !hasBottomSpacing,
          'pt-3 px-3 pb-14': hasBottomSpacing,
        },
      ]"
    >
      <button
        v-if="isProjectCollapsible"
        class="inline-flex items-center space-x-0.5"
        :class="{
          'ml-4': !isProjectTopLevel,
        }"
        type="button"
        @click="$emit('toggleProject', projectId)"
      >
        <span class="shrink-0 w-4 h-4"
          ><ChevronRightIcon v-if="isProjectCollapsed" /><ChevronDownIcon
            v-else
        /></span>
        <span>{{ projectName }}</span>
      </button>
      <div v-else :class="isProjectTopLevel ? 'pl-4' : 'pl-8'">
        {{ projectName }}
      </div>
    </td>
    <td
      v-for="year in years"
      :key="year.key"
      :class="[
        'align-top',
        {
          'p-3': !hasBottomSpacing,
          'pt-3 px-3 pb-14': hasBottomSpacing,
        },
      ]"
    >
      <MlDataPointValue
        v-if="values[year.key]"
        :dataPointValueAndType="values[year.key]"
      />
      <span v-else>-</span>
    </td>
  </tr>
</template>
